import { Component, Inject, OnDestroy, OnInit, } from '@angular/core';
import { availableBrandGroups, BrandGroupMap, eroiIds, metricValueDecimalPlaces, tvdData, tvdGroupIds, TvdMetricData, TvdMetricGroup, TvdValuePillar } from '../../types/tvd-types';
import { Subscription } from 'rxjs';
import { FiltersService } from 'src/app/core/services/filters.service';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { Router } from '@angular/router';
import { TVDDistributorService } from '../../services/tvd-distributor.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { cloneDeep } from 'lodash';
import { TVDModel } from '../../types/tvd-model';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';
import { FilterOption } from 'src/app/shared/components/advanced-filters/advanced-filters.component';
import { InfoTooltip } from 'src/app/core/types/tooltip-types';
import { UIPreferencesService } from 'src/app/core/services/ui-preferences.service';
import { TVDService } from '../../services/tvd.service';
import { tvdSummaryPillsConfig } from '../../types/tvd-metric-tiles-config';

@Component({
    selector: 'tvd-distributor',
    templateUrl: './tvd-distributor.component.html',
    styleUrls: ['./tvd-distributor.component.scss'],
    standalone: false
})

export class TvdDistributorComponent implements OnInit, OnDestroy {
  private filters: ContractModel | undefined;
  private filtersSubscription: Subscription | undefined;
  private tvdSubscription: Subscription | undefined;
  private displayInRoundedNumberSubscription: Subscription | undefined;
  private showInEnterpriseTermsSubscription: Subscription | undefined;

  pageTitle : string = "Total Value Delivery";

  tvdOverallData: TvdMetricData = {
     id : 'tvd-overall',
     $type: 'TvdCostMetric',
     brand: undefined,
     sortSequenceNumber: 0,
     valueContribution : '',
     monetaryContribution : ''
   };

  //holds all tvd metric groups 
  tvdMetricGroupsData : TvdMetricGroup[] = [];

  // number of metrics selected
  numMetricsSelected: number = 0; 
  areProductMetricsSelected: boolean = false;

  makingSelectionsState: boolean = false;

  availableBrandGroupTabs = [availableBrandGroups.ALL];
  tvdBrandGroupScope: availableBrandGroups = availableBrandGroups.ALL; //changes depending on which brand group tab is selected
  availableTvdBrands: Record<string, string[]> = {
    [availableBrandGroups.ALL]: [],
    [availableBrandGroups.ECOLAB]: [],
    [availableBrandGroups.DISTRIBUTOR]: []
  };
  tvdCombinedMetricGroupsData: TvdMetricGroup[] = [];

  // To hold tvd metric groups organized by value pillar for easier consumption in html template.
  // The individual metrics in the TvdMetricGroup are filtered by the TvdValuePillar.
  // This should be updated whenever the data, tvdBrandGroupScope, or selectedFilterOptions are updated.
  tvdProductMetricGroupDataByValuePillar: Record<TvdValuePillar, TvdMetricGroup[]> = {
    [TvdValuePillar.BusinessOutcomes ]: [],
    [TvdValuePillar.OperationalPerformance]: [],
    [TvdValuePillar.EnvironmentalImpact]: []
  };

  filterOptions: FilterOption[] = [];
  selectedFilterOptions: { [id: string]: string[] } = {
    [availableBrandGroups.ALL]: [],
    [availableBrandGroups.ECOLAB]: [],
    [availableBrandGroups.DISTRIBUTOR]: []
  };

  displayInRoundedNumber: boolean = false;
  showInEnterpriseTerms: boolean = false;
  showProductMetrics : boolean = true;
  selectionsPreviewState :boolean = false;

  summaryPillMetrics: string[] = [eroiIds.WATER, eroiIds.ENERGY, eroiIds.SOLID_WASTE];
  valuePillars = Object.values(TvdValuePillar); // to preserve the order they are declared in the enum
  expandedValuePillarId: TvdValuePillar | null = null;

  //expose enums for html
  TvdValuePillar = TvdValuePillar;
  infoTooltipType = InfoTooltip;

  /**
   * MIGRATION NOTE:
   *
   * This flag is a temporary mechanism for controlling render timing while
   * the component prepares its data in multiple steps.
   *
   * FINAL GOAL:
   * Remove isDataReady entirely and align this component with
   * TotalValueDeliveryComponent, where rendering is driven solely by
   * data-derived logic (e.g. hasMetricsForPillar).
   *
   * Target pattern:
   *
   *   [noMetricsForValuePillar]="!hasMetricsForPillar(pillar)"
   *
   * without any additional readiness guards in the template.
   *
   * To achieve this:
   * - Ensure updateData() fully prepares ALL required state in a single pass
   * - Guarantee that by the time data is bound, counts and metrics are valid
   *
   * Once those conditions are met, this flag becomes unnecessary and should be removed.
   */
  isDataReady = false;

  /**
   * Stores precomputed counts for each value pillar.
   *
   * Using Record<TvdValuePillar, number> ensures:
   *   - Type safety (only valid enum keys allowed)
   *   - No string literals / magic keys
   */
  valuePillarCounts: Record<TvdValuePillar, number> = {
    [TvdValuePillar.BusinessOutcomes]: 0,
    [TvdValuePillar.OperationalPerformance]: 0,
    [TvdValuePillar.EnvironmentalImpact]: 0
  };
  
  constructor(
    private router: Router,
    private filtersService: FiltersService,
    private tvdDistributorService: TVDDistributorService,
    private mixpanelService: MixpanelService,
    private _uiPreferencesService: UIPreferencesService,
    private tvdService: TVDService,
    @Inject(FEATURE_FLAGS) private featureFlags: FeatureFlags
  ){}

  ngOnInit() {
    if (this.featureFlags.enableTvdBrandGroupTabs) {
      this.availableBrandGroupTabs = [availableBrandGroups.ALL, availableBrandGroups.ECOLAB, availableBrandGroups.DISTRIBUTOR];
    } else {
      this.availableBrandGroupTabs = [availableBrandGroups.ALL];
    }
    this.filtersSubscription = this.filtersService.selectedFiltersObservable.subscribe(f => this.filters = f);
    this.tvdSubscription = this.tvdDistributorService.tvdObservable.subscribe((t : TVDModel | undefined) => {
      this.updateData(t?.tvdMetricGroups);
    });

    this.displayInRoundedNumberSubscription = this._uiPreferencesService.displayRoundedValuesObservable.subscribe(f => this.displayInRoundedNumber = f);
    this.showInEnterpriseTermsSubscription = this.tvdService.showInEnterpriseTermsObservable.subscribe(f => this.showInEnterpriseTerms = f);

    // only call for data retrieval if TVD page hasn't already been loaded
    if(this.tvdMetricGroupsData.length === 0)
      this.retrieveData();

    this.mixpanelService.trackEventWithFilters('TVD Dashboard Loaded', this.filters, this.pageTitle);
    this.mixpanelService.initializeActiveDurationTracking(); // start active duration tracking
    this.tvdService.setTvdInitialLoadComplete(true);
  }

  ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.tvdSubscription?.unsubscribe();
    this.displayInRoundedNumberSubscription?.unsubscribe();
    this.showInEnterpriseTermsSubscription?.unsubscribe();
  }

  /**
   * Retrieve Product and Business metrics from service if filters are applied
   */
  async retrieveData() {
    if(this.filters && this.filters.division == 'Distributor') {
      if (this.tvdMetricGroupsData.length === 0) {
        await this.tvdDistributorService.setDefaultTVDModel();
      }
    }
    else {
      alert('Please apply filters before navigating to Total Value Delivery')
      this.router.navigate(['/filter']);
    }
  }

  /**
   * Updates the metric group collection with Product and Business Metrics
   * Triggered when loading page or when presentation metrics are updated
   */
  updateData(metricGroups: TvdMetricGroup[] = []) {

    //create deep clone because we modify it in the component and don't want to modify the original
    let newGroupsData = metricGroups ? cloneDeep(metricGroups) : [];

    //maintain the selection state of the groups when data updates
    if(this.tvdMetricGroupsData.length > 0) {
      newGroupsData.forEach(newGroup => {
        let oldGroup = this.tvdMetricGroupsData.find(group => group.id === newGroup.id);
        if(oldGroup) newGroup.groupSelected = oldGroup.groupSelected;
      });
    }

    this.tvdMetricGroupsData = newGroupsData;
    this.tvdCombinedMetricGroupsData = this.combineMetricGroupsData(newGroupsData);

    this.updateTvdProductMetricGroupDataByValuePillar();

    // recompute after data arrives
    this.computeAllCounts();
    
    this.tvdDistributorService.setSelectedFilterOptions(this.selectedFilterOptions);
    // Find the group containing the TVD Overall metric
    let productMetrics = this.tvdMetricGroupsData.find(group =>
      group.id === tvdGroupIds.DEFAULT
    );

    // Find TVD Overall metric and assign it to the component variable
    if (productMetrics) {
      this.setTvdOverallMetricData(productMetrics);
    }

    this.availableBrandGroupTabs.forEach(tab => {
      const brands = this.getBrandsForGroup(this.tvdMetricGroupsData, tab);
      brands.forEach(brand => {
        this.availableTvdBrands[tab].push(brand);
      });
      this.setFilterOptions(tab, this.availableTvdBrands[tab].map(brand => ({ val: brand, checked: false })));
    });

    this.isDataReady = this.tvdMetricGroupsData.length > 0;
  }

  //adjusts UI view on which eroi metrics are shown
  changeScope(brandGroup: availableBrandGroups) {
    this.tvdBrandGroupScope = brandGroup;
    this.tvdDistributorService.setSelectedBrandGroup(brandGroup);

    // Find TVD Overall metric and assign it to the component variable
    let metricGroup = this.tvdMetricGroupsData.find(group => group.id === tvdGroupIds.DEFAULT && group.brandGroup === brandGroup);
    if(metricGroup){
      this.setTvdOverallMetricData(metricGroup)
    }
    this.updateTvdProductMetricGroupDataByValuePillar();

    this.makingSelectionsState = false;

    // recompute because scope affects filtering
    this.computeAllCounts();
  }

  // Find TVD Overall metric and assign it to the component variable
  setTvdOverallMetricData(productMetrics: TvdMetricGroup) {
    let tvdOverallMetric = productMetrics?.summMetrics.find(metric => metric.id === eroiIds.TOTALVALUEDELIVERED);
    if(tvdOverallMetric) this.tvdOverallData = tvdOverallMetric;
  }

  combineMetricGroupsData(metricGroups: TvdMetricGroup[]): TvdMetricGroup[] {
    const formatCostValue = (num: number, decimals: number = 0): string =>
      num.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    const formatGrowthPercentage = (
      currentValue: number | undefined,
      previousValue: number | undefined,
      decimals: number = 0,
      // TODO: Discuss with product what the baseline should be
      /*

      */
      baselineValue: number = 1
    ): string => {
      if (currentValue === undefined || previousValue === undefined) {
        return "N/A";
      }

      if (isNaN(currentValue) || isNaN(previousValue) || isNaN(baselineValue)) {
        throw new Error("All inputs must be valid numbers.");
      }

      const effectivePrevious = previousValue === 0 ? baselineValue : previousValue;
      const growth = ((currentValue - effectivePrevious) / effectivePrevious) * 100;

      return `${growth.toFixed(decimals)}%`;
    };

    // Get distinct brand groups
    const distinctBrandGroups = Array.from(new Set(metricGroups.map(item => item.brandGroup)));

    const combinedMetricGroupsData: TvdMetricGroup[] = [];
    distinctBrandGroups.forEach(brandGroup => {
      const branGroupMetricGroupsData = metricGroups.filter(group => group.brandGroup === brandGroup);
      const branGroupCombinedMetricGroupsData: TvdMetricGroup[] = Object.values(
        branGroupMetricGroupsData.reduce((acc, group) => {
          if (!acc[group.id]) {
            acc[group.id] = {
              id: group.id,
              title: group.title,
              brandGroup: group.brandGroup,
              groupSelected: group.groupSelected,
              summMetrics: group.summMetrics,
              indvMetrics: [] // Will be populated later with the accumulated data
            };
          }

          // Filter and parse values then accumulate individual metrics
          group.indvMetrics.sort((a, b) => a.sortSequenceNumber - b.sortSequenceNumber).forEach(data => {
            if (this.selectedFilterOptions[brandGroup].length === 0 ||
               (data.brand && this.selectedFilterOptions[brandGroup].includes(data.brand))) {
              const existing = acc[group.id].indvMetrics.find(d => d.id === data.id);
              const monetaryContribution = parseFloat(data.monetaryContribution === undefined ? "0" : data.monetaryContribution.replace(/,/g, ""));
              const valueContribution = parseFloat(data.valueContribution === undefined ? "0" : data.valueContribution.replace(/,/g, ""));
              const modifiedMonetaryContribution = parseFloat(data.modifiedMonetaryContribution === undefined ? "0" : data.modifiedMonetaryContribution.replace(/,/g, ""));
              const modifiedValueContribution = parseFloat(data.modifiedValueContribution === undefined ? "0" : data.modifiedValueContribution.replace(/,/g, ""));
              const currentValue = data.currentValue ?? 0;
              const previousValue = data.previousValue ?? 0;

              if (existing) {
                const existingValueContribution = parseFloat(existing.valueContribution === undefined ? "0" : existing.valueContribution.replace(/,/g, ""));
                existing.currentValue = (existing.currentValue ?? 0) + currentValue;
                existing.previousValue = (existing.previousValue ?? 0) + previousValue;
                if (existing['$type'] === 'TvdGrowthMetric') {
                  existing.valueContribution = formatGrowthPercentage(existing.currentValue, existing.previousValue, metricValueDecimalPlaces[existing.id as eroiIds] ?? 0);
                }
                else {
                  existing.valueContribution = formatCostValue(existingValueContribution + valueContribution, metricValueDecimalPlaces[existing.id as eroiIds] ?? 0);
                }
                const existingMonetaryContribution = parseFloat(existing.monetaryContribution === undefined ? "0" : existing.monetaryContribution.replace(/,/g, ""));
                existing.monetaryContribution = formatCostValue(existingMonetaryContribution + monetaryContribution);
                const existingModifiedMonetaryContribution = parseFloat(existing.modifiedMonetaryContribution === undefined ? "0" : existing.modifiedMonetaryContribution.replace(/,/g, ""));
                existing.modifiedMonetaryContribution = formatCostValue(existingModifiedMonetaryContribution + modifiedMonetaryContribution);
                const existingModifiedValueContribution = parseFloat(existing.modifiedValueContribution === undefined ? "0" : existing.modifiedValueContribution.replace(/,/g, ""));
                existing.modifiedValueContribution = formatCostValue(existingModifiedValueContribution + modifiedValueContribution);
              } else {
                if (data['$type'] === 'TvdGrowthMetric') {
                  data.valueContribution = formatGrowthPercentage(data.currentValue ?? 0, data.previousValue ?? 0, metricValueDecimalPlaces[data.id as eroiIds] ?? 0);
                }
                else {
                  data.valueContribution = formatCostValue(valueContribution, metricValueDecimalPlaces[data.id as eroiIds] ?? 0);
                }
                acc[group.id].indvMetrics.push({
                  ...data,
                  monetaryContribution: formatCostValue(monetaryContribution),
                  modifiedMonetaryContribution: formatCostValue(modifiedMonetaryContribution),
                  modifiedValueContribution: formatCostValue(modifiedValueContribution, metricValueDecimalPlaces[data.id as eroiIds] ?? 0),
                  currentValue: data.currentValue ?? 0 + currentValue,
                  previousValue: data.previousValue ?? 0 + previousValue
                });
              }
            }
          });

          return acc;
        }, {} as Record<string, TvdMetricGroup>)
      );
      combinedMetricGroupsData.push(...branGroupCombinedMetricGroupsData);
    });

    return combinedMetricGroupsData;
  }

  get tvdMetricGroupsDataByBrand(): TvdMetricGroup[] {
    return (this.tvdCombinedMetricGroupsData.filter(group => group.brandGroup === this.tvdBrandGroupScope) || []) as TvdMetricGroup[];
  }

  getBrandGroupTitle(brandGroup: string): string {
    return BrandGroupMap[brandGroup] || brandGroup;
  }

  /**
   * Gets the subtitle that displays filters selected
   * @returns the display string for the filters subtitle
   */
  getFilterSubTitle() {
    let subtitle: string = 'Accounts: 0';

    if (this.filters?.accts) {
      let acctLabel = `Accounts: ${this.filters.accts?.length}`
      subtitle = `${acctLabel}`;
    }

    return subtitle;
  }

  /**
   * Selects a metric group and calls to update the selected metrics.
   * Since tvdMetricGroupsData can contain multiple groups with the same title & id (differentiated by brand group),
   * we find all groups with the same title and toggle their selection state. 
   * In other words, adding the "Product Metrics" (default_tvd) adds all tvdMetricGroups with that title to the presentation.
   * @param groupTitle name of the metric group whose selection has been changed
   */
  handleMetricGroupSelectionChange(groupTitle: string) {
    this.tvdMetricGroupsData.forEach(metricGroup => {
      if(metricGroup.title === groupTitle){
        metricGroup.groupSelected = !metricGroup.groupSelected;
      }
    });
    this.tvdCombinedMetricGroupsData.forEach(metricGroup => {
      if(metricGroup.title === groupTitle){
        metricGroup.groupSelected = !metricGroup.groupSelected;
      }
    });

    this.areProductMetricsSelected = this.tvdMetricGroupsData.some(group => group.groupSelected && group.brandGroup === availableBrandGroups.ALL && group.id === tvdGroupIds.DEFAULT);
    // update number of metrics selected
    this.numMetricsSelected = this.tvdMetricGroupsData.filter(group => group.groupSelected && group.brandGroup === availableBrandGroups.ALL).reduce((acc, group) => acc + group.indvMetrics.length, 0);
  }

  /**
   * Toggles whether user is selecting metrics or not
   */
  toggleMakingSelectionsState() {
    this.makingSelectionsState = !this.makingSelectionsState;
  }

  /**
   * Sends the selected metrics to the service to include them in presentation download
   */
  updatePresentationMetrics() {
    // If no metrics are selected for the ALL brand group in the Add to Presentation experience, treat all metrics as selected.
    if (this.numMetricsSelected === 0) {
      this.tvdMetricGroupsData
        .filter(group => group.brandGroup === availableBrandGroups.ALL)
        .forEach(group => { group.groupSelected = true; });
      this.numMetricsSelected = this.tvdMetricGroupsData
        .filter(group => group.groupSelected && group.brandGroup === availableBrandGroups.ALL)
        .reduce((acc, group) => acc + group.indvMetrics.length, 0);
    }
    this.toggleMakingSelectionsState();
    this.tvdDistributorService.updatePresentationMetrics(this.tvdMetricGroupsData);
  }

  setFilterOptions(tab: string, options: { val: string; checked: boolean }[]) {
    if (options.length > 0) {
      let tempFilters: { title: string, options: { val: string; checked: boolean }[], inputType:string}[] = [];
      tempFilters.push({ title: tab, options: options, inputType: 'checkbox' });
      this.filterOptions = tempFilters;
    }
  }

  getBrandsByGroup(metricGroups: TvdMetricGroup[]): Record<string, Set<string>> {
    const brandsByGroup: Record<string, Set<string>> = {};

    for (const group of metricGroups) {
      const allMetrics = [...group.indvMetrics, ...group.summMetrics];

      if (!brandsByGroup[group.brandGroup]) {
        brandsByGroup[group.brandGroup] = new Set();
      }

      for (const metric of allMetrics) {
        if (metric.brand) {
          brandsByGroup[group.brandGroup].add(metric.brand);
        }
      }
    }

    return brandsByGroup;
  }

  getBrandsForGroup(metricGroups: TvdMetricGroup[], targetGroup: string): string[] {
    const brandSet = new Set<string>();

    for (const group of metricGroups) {
      if (group.brandGroup === targetGroup) {
        const allMetrics = [...group.indvMetrics, ...group.summMetrics];

        for (const metric of allMetrics) {
          if (metric.brand) {
            brandSet.add(metric.brand);
          }
        }
      }
    }

    return Array.from(brandSet);
  }

  updateSelectedOptions() {
    this.tvdCombinedMetricGroupsData = this.combineMetricGroupsData(this.tvdMetricGroupsData);
    this.updateTvdProductMetricGroupDataByValuePillar();
    this.tvdDistributorService.setSelectedFilterOptions(this.selectedFilterOptions);
  }

  //#region Refreshed UI Methods

  // For presentation preview, just show the ALL brand group product metrics 
  get tvdMetricGroupsDataForPresentationPreview(): TvdMetricGroup | undefined{
    //If group is selected, user should see those metrics in the presentation preview
    if(this.tvdCombinedMetricGroupsData.find(group => group.brandGroup === availableBrandGroups.ALL && group.id == tvdGroupIds.DEFAULT)?.groupSelected){
      return (this.tvdCombinedMetricGroupsData.find(group => group.brandGroup === availableBrandGroups.ALL && group.id == tvdGroupIds.DEFAULT) || []) as TvdMetricGroup;
    }
    else{
      return undefined;
    }   
  }


  private updateTvdProductMetricGroupDataByValuePillar() {
    this.tvdProductMetricGroupDataByValuePillar[TvdValuePillar.BusinessOutcomes] = this.getTvdMetricGroupsDataByValuePillar(TvdValuePillar.BusinessOutcomes);
    this.tvdProductMetricGroupDataByValuePillar[TvdValuePillar.OperationalPerformance] = this.getTvdMetricGroupsDataByValuePillar(TvdValuePillar.OperationalPerformance);
    this.tvdProductMetricGroupDataByValuePillar[TvdValuePillar.EnvironmentalImpact] = this.getTvdMetricGroupsDataByValuePillar(TvdValuePillar.EnvironmentalImpact);
  }

  /**
   * Get the Tvd Metric Groups for the Product Metrics group (default_tvd),
   * with the indvMetrics filtered by Value Pillar.
   * @param valuePillar 
   * @returns 
   */
  getTvdMetricGroupsDataByValuePillar(valuePillar: TvdValuePillar): TvdMetricGroup[] {
    let retval =  (this.tvdCombinedMetricGroupsData.filter(group => group.brandGroup === this.tvdBrandGroupScope) || []) as TvdMetricGroup[];
    let defaultGroup = retval.find(group => group.id === tvdGroupIds.DEFAULT);
    
    const metricIdsByValuePillar: Record<TvdValuePillar, string[]> = {
      [TvdValuePillar.BusinessOutcomes]: [eroiIds.EMPLOYEESAFETY],
      [TvdValuePillar.OperationalPerformance]: [eroiIds.PRODUCTIVITY, eroiIds.ASSETS, eroiIds.COSTAVOIDANCE, eroiIds.PORTFOLIO],
      [TvdValuePillar.EnvironmentalImpact]: [eroiIds.SOLID_WASTE, eroiIds.WATER, eroiIds.ENERGY, eroiIds.GREENHOUSEGASES, eroiIds.PLASTIC, eroiIds.CARDBOARD]
    };
    
    if (!defaultGroup) {
      return [];
    }

    const filteredGroup: TvdMetricGroup = {
      ...defaultGroup,
      indvMetrics: defaultGroup.indvMetrics.filter(metric => metricIdsByValuePillar[valuePillar].includes(metric.id))
    };

    return [filteredGroup];
  }

  getDistributorBusinessMetrics(): TvdMetricGroup[] {
    let retval =  (this.tvdCombinedMetricGroupsData.filter(group => group.brandGroup === this.tvdBrandGroupScope) || []) as TvdMetricGroup[];
    let defaultGroup = retval.find(group => group.id === tvdGroupIds.DISTRIBUTOR);
     
    if (!defaultGroup) {
      return [];
    }

    return [defaultGroup];
  }

  toggleRounding() {
    this._uiPreferencesService.setDisplayRoundedValues(!this.displayInRoundedNumber);
  }

  toggleShowEnterpriseTerms() {
    this.tvdService.setShowInEnterpriseTerms(!this.showInEnterpriseTerms);
  }

  toggleProductBusinessMetrics(){
    this.showProductMetrics = !this.showProductMetrics;
  }

  getSummaryPillData(metricId: string): TvdMetricData[]{
    const metricGroup = tvdSummaryPillsConfig.find(
      c => c.metricGroupId === metricId
    );
  
    if(!metricGroup) {
      console.warn(`Metric group not found for: ${metricId}`);
      return [];
    }

    let productMetrics = this.tvdMetricGroupsData.find(group =>
      group.id === tvdGroupIds.DEFAULT && group.brandGroup === this.tvdBrandGroupScope
    );
      
    return productMetrics?.indvMetrics.filter(metric => metric.id === metricId) ?? [];
  }

  onValuePillarToggle(expandedValuePillar: TvdValuePillar){
    this.expandedValuePillarId = this.expandedValuePillarId === expandedValuePillar ? null : expandedValuePillar;
  }

  trackByValuePillar(index: number, valuePillar: TvdValuePillar) {
    return valuePillar;
  }
  
  /**
   * NOTE:
   * This method is intended to fully replace template readiness checks
   * (e.g. isDataReady, data length guards).
   *
   * Long-term goal is for templates to rely ONLY on this method when
   * determining pillar state.
   */
  hasMetricsForPillar(pillar: TvdValuePillar): boolean {
    return this.countAvailableValuePillarMetrics(pillar) > 0;
  }

  /**
   * IMPORTANT:
   * This is now a simple lookup — NOT a computation.
   *
   * WHY:
   * Angular executes template methods frequently during change detection.
   * Keeping this as a lookup guarantees:
   *   - consistent results
   *   - no performance issues
   *   - no timing bugs
   */
  countAvailableValuePillarMetrics(pillar: TvdValuePillar): number {
    return this.valuePillarCounts[pillar];
  }

  
  cancelMakingSelectionsState() {
    this.makingSelectionsState = false;
  }

  /**
   * Reverts any pending selection changes by setting the groupSelected value back to false.
   * If a group was selected but not yet added to the presentation, this will unselect it.
   */
  clearSelectedTilesForPresentation()
  {
    this.tvdMetricGroupsData.forEach(metricGroup => metricGroup.groupSelected = false);
    this.tvdCombinedMetricGroupsData.forEach(metricGroup => metricGroup.groupSelected = false);
    // update number of metrics selected
    this.numMetricsSelected = 0; 
    this.areProductMetricsSelected = false
    this.cancelMakingSelectionsState();
  }

  toggleSelectionsPreviewState() {
    this.selectionsPreviewState = !this.selectionsPreviewState;
  }

  /**
   * Computes counts once after data is available.
   *
   * WHY:
   * Avoid computing inside template (Angular calls methods many times).
   * This ensures stable, correct values.
   */
  computeAllCounts(): void {
    this.valuePillarCounts = {
      [TvdValuePillar.BusinessOutcomes]: this.computeCount(TvdValuePillar.BusinessOutcomes),
      [TvdValuePillar.OperationalPerformance]: this.computeCount(TvdValuePillar.OperationalPerformance),
      [TvdValuePillar.EnvironmentalImpact]: this.computeCount(TvdValuePillar.EnvironmentalImpact)
    };
  }

  /**
   * Computes the metric count for a given value pillar.
   *
   * WHY:
   * Centralizes all counting logic in one place and ensures it is
   * only executed when data is ready (not during template rendering).
   */
  private computeCount(pillar: TvdValuePillar): number {
    if (!this.tvdMetricGroupsData || this.tvdMetricGroupsData.length === 0) {
      return 0;
    }

    const group = this.tvdMetricGroupsData.find(g =>
      g.id === tvdGroupIds.DEFAULT &&
      g.brandGroup === this.tvdBrandGroupScope
    );
  
    if (!group) return 0;

    // Choose metric IDs based on pillar
    let applicableMetricIds: eroiIds[] = [];

    switch (pillar) {
      case TvdValuePillar.EnvironmentalImpact:
        applicableMetricIds = [
          eroiIds.WATER,
          eroiIds.ENERGY,
          eroiIds.GREENHOUSEGASES,
          eroiIds.GREENHOUSEGASES_ENERGY,
          eroiIds.GREENHOUSEGASES_TRANSPORT,
          eroiIds.GREENHOUSEGASES_PACKAGING,
          eroiIds.CARDBOARD,
          eroiIds.PLASTIC
        ];
        break;

      case TvdValuePillar.BusinessOutcomes:
        applicableMetricIds = [
          eroiIds.EMPLOYEESAFETY
        ];
        break;

      case TvdValuePillar.OperationalPerformance:
        applicableMetricIds = [
          eroiIds.PRODUCTIVITY,
          eroiIds.ASSETS,
          eroiIds.COSTAVOIDANCE,
          eroiIds.PORTFOLIO
        ];
        break;
    }

    const metrics = group.indvMetrics.filter(metric =>
      applicableMetricIds.includes(metric.id as eroiIds)
    );

    return metrics.length
  }

  //#endregion
}
